import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  adoptHostSession,
  authenticate,
  clearAuth,
  getStoredAuthToken,
  getStoredJwtToken,
  getStoredUsername,
  notifySessionEnded,
  scheduleProactiveRefresh,
  setForceLogoutHandler,
  stopProactiveRefresh,
} from '@/api/services/auth.service'
import { getQueryClient } from '@/api/http/query-client'
import { logger } from '@/utils/logger'
import { AuthContext, type AuthContextValue } from './auth-context'

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [username, setUsername] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Stable ref so the force-logout callback never causes re-registration.
  const logoutRef = useRef<() => void>(() => {})

  // Bootstrap: register the force-logout handler FIRST, then check for an
  // existing session and schedule proactive refresh.  Both must live in the
  // same effect because `scheduleProactiveRefresh` can call
  // `triggerForceLogout` synchronously when the JWT is already expired.
  useEffect(() => {
    setForceLogoutHandler(() => {
      logger.warn('Force logout triggered — token expired or refresh failed')
      // Embedded, the host signs the user out instead (see components/pages/embed). Notify before
      // logout so the embed page can unmount the editor first: clearing the query cache under a
      // mounted home made it react to the emptied data (WMPCM-12160).
      const handledByHost = notifySessionEnded()

      logoutRef.current()
      if (!handledByHost) window.location.href = '/login'
    })

    const storedToken = getStoredAuthToken()
    const storedUsername = getStoredUsername()

    if (storedToken) {
      setToken(storedToken)
      setUsername(storedUsername)
      setIsAuthenticated(true)
      logger.debug('Restored authentication from session storage')

      const jwt = getStoredJwtToken()

      if (jwt) scheduleProactiveRefresh(jwt)
    }

    setIsLoading(false)

    return () => setForceLogoutHandler(null)
  }, [])

  const login = useCallback(
    async (loginUsername: string, loginPassword: string): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        const authToken = await authenticate(loginUsername, loginPassword)

        setToken(authToken)
        setUsername(loginUsername)
        setIsAuthenticated(true)

        const jwt = getStoredJwtToken()

        if (jwt) scheduleProactiveRefresh(jwt)

        logger.info(`User ${loginUsername} logged in successfully`)
        return true
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed'

        setError(errorMessage)
        logger.error('Login failed:', err)
        return false
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const adoptSession = useCallback(
    async (wpcauth: string, sessionUsername: string): Promise<boolean> => {
      setIsLoading(true)
      setError(null)

      try {
        await adoptHostSession(wpcauth, sessionUsername)

        setToken(wpcauth)
        setUsername(sessionUsername)
        setIsAuthenticated(true)

        const jwt = getStoredJwtToken()

        if (jwt) scheduleProactiveRefresh(jwt)

        logger.info('Signed in with the embedding host session')
        return true
      } catch (err) {
        setToken(null)
        setUsername(null)
        setIsAuthenticated(false)
        setError(err instanceof Error ? err.message : 'Session handoff failed')
        logger.error('Adopting the embedding host session failed:', err)
        return false
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const logout = useCallback(() => {
    stopProactiveRefresh()
    getQueryClient().clear()
    clearAuth()
    setToken(null)
    setUsername(null)
    setIsAuthenticated(false)
    setError(null)
    logger.info('User logged out')
  }, [])

  // Keep logoutRef in sync so the force-logout handler always calls the latest logout.
  logoutRef.current = logout

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    username,
    token,
    error,
    login,
    adoptSession,
    logout,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
